import * as Business         from '../business';
import * as ContactActions   from '../contacts/actions';
import * as ContactChannels  from '../contacts/channels';
import * as ContactSelectors from '../contacts/selectors';
import * as Forms            from '../components/forms';
import * as User             from '../user';
import { compose }           from 'redux';
import { connect }           from 'react-redux';
import { ExternalLink }      from 'react-feather';
import { format }            from 'date-fns';
import { formatDistance }    from 'date-fns';
import { openExternalLink }  from '../components/link';
import { parseISO }          from 'date-fns';
import { useState }          from 'react';
import classnames            from 'classnames';
import ConfirmButton         from '../components/confirm_button';
import FetchFromServer       from '../components/fetch_from_server';
import getFirstActivityText  from '../contacts/get_first_activity_text';
import isCustomerBlocked     from '../contacts/is_customer_blocked';
import React                 from 'react';
import rollbar               from '../rollbar';
import Spinner               from '../components/spinner';
import StarRating            from '../components/star_rating';
import SwitchControl         from '../components/switch_control';

import './info_page.less';


export default connect(
  (state, { businessID, contactID }) => ({
    businessID,
    statuses:  ContactSelectors.getContactStatuses(state, contactID),
    contact:   ContactSelectors.getContact(state, contactID),
    provider:  Business.getCurrentProvider(state, businessID),
    isManager: User.isManager(state, businessID)
  }),
  (dispatch, { businessID, contactID, navigateTo }) => ({
    load:                          () => dispatch(ContactActions.readContact(contactID, businessID)),
    save:                          compose(dispatch, ContactActions.updateContact),
    updateContactFollowUpStatus:   compose(dispatch, ContactActions.updateContactFollowUpStatus),
    updateContactBlockStatus:      compose(dispatch, ContactActions.updateContactBlockStatus),
    updateContactPreferredChannel: compose(dispatch, ContactActions.updateContactPreferredChannel),
    deleteContact:                 compose(dispatch, ContactActions.deleteContact),
    getIntegrationURL:             compose(dispatch, ContactActions.getIntegrationURL),
    navigateTo
  })
)(InfoPage);


export function InfoPage({
  contact,
  statuses,
  load,
  save,
  deleteContact,
  updateContactFollowUpStatus,
  updateContactBlockStatus,
  updateContactPreferredChannel,
  navigateTo,
  isManager,
  getIntegrationURL
}) {
  async function onDelete() {
    await deleteContact(contact);
    navigateTo('../');
  }

  const { createStatus, updateStatus } = statuses;
  const contactID                      = contact?.id;
  const hasIntegration                 = contact?.source?.name && contact?.publicID && contact.publicID !== contactID;
  // We currently only support ShopWare in the "View in" integration.
  const hasShopWareIntegration = hasIntegration && contact?.source?.name === 'shopWare';

  return (
    <Forms.EditPage
      record={contact}
      title={contact?.name.full}
      save={save}
      validate={validate}>
      {editPage => (
        <FetchFromServer statuses={statuses} load={load}>
          {contact && editPage.readOnly && (
            <ContactFollowUpStatusToggle
              contact={contact}
              save={updateContactFollowUpStatus}
              createStatus={createStatus}
              updateStatus={updateStatus}
            />
          )}
          {contact && (
            <WithContact
              contact={contact}
              isManager={isManager}
              onDelete={onDelete}
              {...editPage}
            />
          )}
          {contact && editPage.readOnly && hasShopWareIntegration && (
            <OpenInExternalIntegrationLink {...{
              getIntegrationURL,
              name: 'Shop-Ware',
              contactID
            }}
            >
            </OpenInExternalIntegrationLink>
          )}
          {contact && editPage.readOnly && (
            <ContactPreferredChannelToggle {...{
              contact,
              updateContactPreferredChannel,
              createStatus,
              updateStatus
            }}/>
          )}
          {contact && editPage.readOnly && (
            <ContactBlockToggle
              contact={contact}
              save={updateContactBlockStatus}
              createStatus={createStatus}
              updateStatus={updateStatus}
            />
          )}
        </FetchFromServer>
      )}
    </Forms.EditPage>
  );
}


function OpenInExternalIntegrationLink({ getIntegrationURL, contactID, name }) {
  const [ isLoading, setIsLoading ] = useState(false);

  async function onClick() {
    setIsLoading(true);

    try {
      const url = await getIntegrationURL({ customerID: contactID, showProgress: false });
      openExternalLink(url);
    } catch (error) {
      rollbar.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  function OpenIn(title) {
    return (
      <a className="integration-url-content" onClick={onClick}>
        <ExternalLink size={18} className="integration-url-icon"/>
        View in {title}
      </a>
    );
  }

  function OpeningIn(title) {
    return (
      <div className="integration-url-content">
        <Spinner size={16} className="integration-url-icon"/>
        Opening in {title}
      </div>
    );
  }

  return (
    <div className="integration-url block">
      {isLoading ? OpeningIn(name) : OpenIn(name)}
    </div>
  );
}


function ContactFollowUpStatusToggle({ contact, save, createStatus, updateStatus }) {
  const inProgress                         = createStatus.pending || updateStatus.pending;
  const { description, disabled, checked } = getFollowUpContext(contact);

  return (
    <div className="follow-up list block">
      <ul>
        <li className="item-content">
          <SwitchItem
            disabled={inProgress || disabled}
            label="Automated Follow Ups"
            description={description}
            checked={checked}
            save={value => save(contact, value)}
          />
        </li>
      </ul>
    </div>
  );
}

function ContactBlockToggle({ contact, save, createStatus, updateStatus }) {
  const inProgress                         = createStatus.pending || updateStatus.pending;
  const { description, disabled, checked } = getBlockedContext(contact);

  return (
    <div className="block-contact list block">
      <ul>
        <li className="item-content">
          <SwitchItem
            disabled={inProgress || disabled}
            label="Block Contact"
            description={description}
            checked={checked}
            save={value => save(contact, value)}
          />
        </li>
      </ul>
    </div>
  );
}

function ContactPreferredChannelToggle({ contact, updateContactPreferredChannel, createStatus, updateStatus }) {
  const inProgress             = createStatus.pending || updateStatus.pending;
  const availableChannels      = contact?.contact?.channels?.available;
  const preferredChannel       = contact?.contact?.channels?.preferred;
  const isSMS                  = preferredChannel === 'sms';
  const canSetPreferredChannel = availableChannels && availableChannels.length > 1;

  function onClick(value) {
    updateContactPreferredChannel(contact, value);
  }

  if (canSetPreferredChannel) {
    return (
      <div className="preferred-channel list block">
        <ul>
          <li className="item-content">
            <div className="item-inner">
              <div className="item-title">
                Preferred Delivery Mechanism
                <div className="item-subtitle">
                  {isSMS && 'We will text this contact when possible.  Email will still be used for review follow ups, or if text messaging fails.'}
                  {!isSMS && 'We will only send email messages to this contact.'}
                </div>
              </div>
              <div className="item-after">
                <ButtonGroup {...{
                  onClick,
                  buttons: [
                    { label: 'Email', value: 'email' },
                    { label: 'Text', value: 'sms' }
                  ],
                  selectedValue: preferredChannel,
                  disabled:      inProgress
                }}/>
              </div>
            </div>
          </li>
        </ul>
      </div>
    );
  } else
    return null;
}

function SwitchItem({ disabled, label, description, checked, save }) {
  return (
    <div className="item-inner">
      <div className="item-title">
        <span>{label}</span>
        <div className="item-subtitle">{description}</div>
      </div>
      <div className="item-after">
        <SwitchControl
          checked={checked}
          save={save}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function getFollowUpContext(contact) {
  const description          = 'Contact is opted into automated messages';
  const isActive             = contact.status === 'active' || contact.status === 'lead';
  const isBlocked            = isCustomerBlocked(contact);
  const isSubscribed         = contact.contact.subscribed;
  const hasFollowUpsEnabled  = !!contact.contact.followUp;
  const hasAvailableChannels = ContactChannels.hasAvailable(contact);

  if (isBlocked) {
    // Customer is blocked, so automated followups are disabled
    return {
      description: 'Contact is blocked.',
      checked:     false,
      disabled:    true
    };
  } else if (!isSubscribed) {
    // Customer unsubscribed themselves, so don't contact them and don't let business re-subscribe them.
    return {
      description: 'Contact has unsubscribed from automated messages, but you can still send messages directly',
      checked:     false,
      disabled:    true
    };
  } else if (!hasAvailableChannels) {
    return {
      description: 'Contact has no email address or mobile phone number',
      checked:     false,
      disabled:    true
    };
  } else if (isActive && hasFollowUpsEnabled) {
    if (contact.nextMessage) {
      const nextMessageIsThankYou = contact.nextMessage.name === 'thankYou';
      const nextMessageTime       = formatDistance(parseISO(contact.nextMessage.time), new Date());

      return {
        description: `Sending a ${nextMessageIsThankYou ? 'review request' : 'follow up'} in ${nextMessageTime}`,
        checked:     true
      };
    } else { // there's no message queued, maybe because they are a lead
      return {
        description,
        checked: true
      };
    }
  } else {
    // Customer is inactive or business turned off their automated messages(customer.contact.followUp: false).
    return {
      description: 'Automated messages are not enabled, but you can still send messages directly',
      checked:     false
    };
  }
}

function getBlockedContext(contact) {
  const isBlocked = isCustomerBlocked(contact);

  if (isBlocked) {
    return {
      description: 'You will not receive any messages sent by this contact.',
      checked:     true
    };
  } else {
    return {
      description: 'Stop receiving messages sent by this contact.',
      checked:     false
    };
  }
}


function WithContact({ contact, onDelete, isManager, ...editPage }) {
  const { isEditing } = editPage;

  return (
    <div className="customer-info">
      <Highlights {...{ contact }}/>
      <Forms.Form {...editPage}>
        <ContactFields {...{ contact }}/>
      </Forms.Form>
      {isEditing && isManager && (<ConfirmButton
        label="Delete Contact"
        className="delete-contact-button"
        confirmTitle="Delete Contact"
        confirmSubtitle={`Delete ${contact.name.full}?`}
        confirmButtonText="Delete"
        onConfirm={onDelete}
        danger={true}
      />)}
    </div>
  );
}

function validate(fields) {
  const emailIsMissing = !fields['contact.email.address'] || fields['contact.email.address'] === '';
  const phoneIsMissing = !fields['contact.phone.number'] || fields['contact.phone.number'] === '';

  if (emailIsMissing && phoneIsMissing)
    throw Error('Please provide an email address and/or phone number');
}

function Highlights({ contact }) {
  const date          = contact.customerSince || contact.createdAt;
  const contactSince  = format(parseISO(date), 'MMMM d, yyyy');
  const firstActivity = `${getFirstActivityText({ customer: contact })} ${contactSince}`;

  return (
    <div className="highlights list">
      <ul>
        <li className="list-group-title">Highlights</li>
        <li className="item-content">{firstActivity}</li>
        <WouldRecommend promoter={contact.promoter}/>
        <ReviewActivities reviews={getReviews(contact.activities)}/>
      </ul>
    </div>
  );
}


function WouldRecommend({ promoter }) {
  if (promoter === null)
    return null;
  else {
    return (
      <li className="item-content">
        Would {promoter === true ? '' : 'not '}recommend your business
      </li>
    );
  }
}


function ReviewActivities({ reviews }) {
  return reviews.map((review, key) => {
    return <ReviewActivity key={key} review={review}/>;
  });
}


function getReviews(activities) {
  const reviews = (activities || [])
    .filter(a => a.type === 'Review')
    .map(activity => activity.review);
  return reviews;
}


const serviceNames = {
  broadly:     'Broadly',
  facebook:    'Facebook',
  google:      'Google',
  tripAdvisor: 'TripAdvisor'
};


function ReviewActivity({ review }) {
  const { rating, service } = review;

  return (
    <li className="item-content">
      {service === 'nextdoor' ? (
        'Recommended you on Nextdoor'
      ) : (
        <div>
          {<StarRating rating={rating}/>} <span>{rating} star rating on {serviceNames[service]}</span>
        </div>
      )}
    </li>
  );
}


function ContactFields({ ...form }) {
  return (
    <div className="list">
      <ul>
        <li className="list-group-title">Contact Info</li>
        <Forms.Error {...form}/>
        <Forms.Input
          {...form}
          name="name.full"
          type="text"
          label="Name"
          required={true}
          autoFocus={true}
          assistType="capitalize"/>

        <Forms.Input {...form} name="contact.phone.number" placeholder="(555) 555-5555" type="tel" maxLength="14" label="Phone Number"/>
        <Forms.Input {...form} label="Email" name="contact.email.address" type="email"/>
      </ul>
    </div>
  );
}


function ButtonGroup({ buttons, selectedValue, disabled, onClick }) {
  return (
    <div className="segmented">
      {buttons && buttons.map(({ label, value }) => {
        const className = classnames('button', 'button-outline', { 'button-active': selectedValue === value });
        return (
          <button
            key={value}
            value={value}
            className={className}
            disabled={disabled}
            onClick={() => onClick(value)}>
            {label}
          </button>
        );
      })}
    </div>
  );
}
